import { inject } from '@angular/core';
import { User } from '@jfw/angular/shared/firebase/auth';
import { CanActivateFn, Router } from '@angular/router';
import { doesUserHaveAccessToRoute } from '@jfw/business-logic';
import { PlatformUserV2 } from '@jfw/types';
import { catchError, combineLatest, map, Observable, of } from 'rxjs';
import { AuthService } from '../../services/auth/auth.service';

export type ModuleGuardDecision =
  | { allowed: true }
  | { allowed: false; redirectTo: '/home'; alertMessage?: string }
  | { allowed: false; redirectTo: null };

// Extracted as a plain function so it's directly unit-testable without
// TestBed/Router/AuthService mocking -- same testability win EXT-6015 got
// from pulling fetchPlatformUserWithRetry out of AuthService.
export function resolveModuleGuardAccess(
  portalUser: PlatformUserV2 | null,
  firebaseUser: User | null,
  currentRoute: string,
): ModuleGuardDecision {
  if (portalUser) {
    if (currentRoute === '/' || currentRoute === '/home') {
      return { allowed: true };
    }

    if (doesUserHaveAccessToRoute(portalUser, currentRoute)) {
      return { allowed: true };
    }

    return {
      allowed: false,
      redirectTo: '/home',
      alertMessage: 'You do not have access to this page. Routing to home.',
    };
  }

  if (!firebaseUser) {
    // Genuinely signed out (e.g. the real-404 path's signOut()) --
    // firebaseUser$'s own redirect logic (auth.service.ts) already sends
    // the user to /sign-in. Nothing further to do here.
    return { allowed: false, redirectTo: null };
  }

  // Still authenticated, but platformUser$ resolved null for a reason
  // short of a real missing document -- fetchPlatformUserWithRetry
  // (EXT-6015) already exhausted retries and deliberately swallows
  // anything but a genuine 404 to null without signing the user out. Don't
  // treat that the same as "confirmed missing" -- just keep them off this
  // one route rather than force a sign-out over a transient error.
  return { allowed: false, redirectTo: '/home' };
}

export const moduleGuard: CanActivateFn = (route): Observable<boolean> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return combineLatest([
    authService.platformUser$,
    authService.firebaseUser$,
  ]).pipe(
    map(([portalUser, firebaseUser]) => {
      const currentRoute = `/${route.routeConfig?.path ?? ''}`;
      const decision = resolveModuleGuardAccess(
        portalUser,
        firebaseUser,
        currentRoute,
      );

      if (decision.allowed) {
        return true;
      }

      if (decision.redirectTo === '/home' && decision.alertMessage) {
        alert(decision.alertMessage);
      }

      if (decision.redirectTo) {
        router.navigate([decision.redirectTo]);
      }

      return false;
    }),
    catchError(() => {
      alert('Permissions error. Routing to home.');
      router.navigate(['/home']);
      return of(false);
    }),
  );
};
